import { FC, useCallback, useLayoutEffect, useReducer } from "react";
import styled from "@emotion/styled";
import {
  MDSButton,
  MDSButtonVariant,
  MDSTextVariant,
  MDSTextField,
  MDSTextWeight,
  MDSText,
  MDSToggle,
} from "@/mds";
import { observer } from "mobx-react-lite";
import {
  StyledSubExplanation,
  StyledHeading,
} from "@/pages/integrations/shared/integrations.styles";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { useAppStore } from "@/store";
import { reducer } from "./smart-formatting-settings.reducer";
import { SaveStatus } from "./smart-formatting-settings.types";

export const SmartFormattingSettings: FC = observer(() => {
  const { store } = useAppStore();

  // Get settings from the store
  const storeProcessingInstructions = store.spaceAccountSettings.processingInstructions;
  const initialHasInstructions = storeProcessingInstructions.length > 0;

  const [state, dispatch] = useReducer(reducer, {
    originalInstructions: storeProcessingInstructions,
    originalIsEnabled: initialHasInstructions,
    currentInstructions: storeProcessingInstructions,
    isEnabled: initialHasInstructions,
    saveStatus: SaveStatus.Idle,
    error: null,
    isEditing: false,
    isResetting: false,
  });

  const {
    originalInstructions,
    originalIsEnabled,
    currentInstructions,
    isEnabled,
    saveStatus,
    error,
    isEditing,
    isResetting,
  } = state;

  useLayoutEffect(() => {
    if (isResetting) dispatch({ type: "CLEAR_RESETTING" });
  }, [isResetting]);

  const isDirty = currentInstructions !== originalInstructions || isEnabled !== originalIsEnabled;

  const validate = (value: string): string | null => {
    if (value.length > 2000) {
      return "Instructions are too long. Please keep them under 2000 characters.";
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validate(currentInstructions);
    if (validationError) {
      return;
    }

    dispatch({ type: "START_SAVING" });

    try {
      const instructionsToSend = isEnabled ? currentInstructions : "";

      await store.spaceAccountSettings.updateSettingsUsingApi({
        voice: {
          processing_instructions: instructionsToSend,
        },
      });

      dispatch({
        type: "SAVE_SUCCESS",
        instructions: instructionsToSend,
        isEnabled: instructionsToSend.length > 0,
      });
    } catch (err) {
      logger.error({
        message: "Failed to save smart formatting settings",
        info: { error: objectModule.safeErrorAsJson(err as Error) },
      });
      dispatch({ type: "SAVE_ERROR", error: "Failed to save settings. Please try again." });
    }
  };

  const handleCancel = () => {
    dispatch({ type: "CANCEL" });
  };

  const handleChange = useCallback((value: string) => {
    const validationError = validate(value);
    dispatch({ type: "SET_INSTRUCTIONS", value, error: validationError });
  }, []);

  const handleSwitchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const isSelected = event.target.checked;
    dispatch({ type: "SET_ENABLED", isEnabled: isSelected });
  }, []);

  const handleExtraContentClick = useCallback(() => {
    if (isEditing) return;
    dispatch({ type: "START_EDITING" });
  }, [isEditing]);

  return (
    <FullWidthContainer>
      <StyledHeading variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
        Smart formatting
      </StyledHeading>
      <StyledSubExplanation variant={MDSTextVariant.ParagraphS}>
        Voice mode captures your thoughts naturally. It automatically detects whether you&apos;re
        dictating a list, recording a meeting, or brainstorming an idea, and formats the note
        accordingly.
      </StyledSubExplanation>

      <StyledOrganizingInstructions isEditing={isEditing}>
        <SwitchContainer
          onClick={() => {
            if (saveStatus !== SaveStatus.Saving) {
              dispatch({ type: "SET_ENABLED", isEnabled: !isEnabled });
            }
          }}
        >
          <StyledToggle
            checked={isEnabled}
            onChange={handleSwitchChange}
            disabled={saveStatus === SaveStatus.Saving}
          />
          <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
            Customize how voice notes are formatted
          </MDSText>
        </SwitchContainer>

        {isEnabled && !isResetting && (
          <ExtraContentContainer>
            <ExtraContent onClickCapture={handleExtraContentClick}>
              <StyledTextArea
                isTextArea
                placeholder={`For meetings, add a "Follow-ups" checklist\nFormat lists with bullets, not numbers\nFor brainstorming, add a one-line summary at the top`}
                value={currentInstructions}
                onChange={handleChange}
                errorMessage={error ?? undefined}
                textAreaProps={{ rows: 4 }}
                isDisabled={saveStatus === SaveStatus.Saving}
              />
            </ExtraContent>
          </ExtraContentContainer>
        )}

        {(isEditing || isDirty) && (
          <ButtonContainer>
            <MDSButton onClick={handleCancel} isDisabled={saveStatus === SaveStatus.Saving}>
              Cancel
            </MDSButton>
            <MDSButton
              variant={MDSButtonVariant.Contrast}
              onClick={handleSave}
              isLoading={saveStatus === SaveStatus.Saving}
              isDisabled={!isDirty || !!error}
            >
              {saveStatus === SaveStatus.Saved ? "Saved" : "Save"}
            </MDSButton>
          </ButtonContainer>
        )}
      </StyledOrganizingInstructions>
    </FullWidthContainer>
  );
});

const FullWidthContainer = styled.div`
  width: 100%;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledTextArea = styled(MDSTextField)`
  width: 100%;
`;

const SwitchContainer = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  height: 40px;
  user-select: none;
`;

const StyledToggle = styled(MDSToggle)``;

const StyledOrganizingInstructions = styled.div<{ isEditing: boolean }>`
  ${({ theme, isEditing }) =>
    isEditing && `background-color: ${theme.colors.background.input.default.default};`}
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme }) => theme.colors.surface.pane};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[3]}`};
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ExtraContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["1"]};
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  padding-left: 44px;
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const ExtraContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["1"]};
  position: relative;
  transform-origin: top;
  transition: transform 0.2s ease-in-out;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[3]};
`;
